import { Tooltip } from 'antd';
import type { FC } from 'react';
import { Fragment, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import DropdownWrapper from '@/shared/DropDownWrapper';
import type { Item } from '@/shared/DropDownWrapper/types';
import { HEADER_UNIT, useMetaContext, WELL_HEIGHT, WELL_WIDTH } from '../../base';
import { useGeoData } from '../../utils';

type WellInfo = {
    x?: number;
    id: number;
    name: string;
    isBadVersion: boolean;
    isVerticalName: boolean;
    lengthening: string;
    altitude: string;
};

type ParentInfo = {
    name: string;
    xStart: number;
    xEnd: number;
};

const ParentFigure: FC<ParentInfo & { y: number }> = ({ name, xStart, xEnd, y }) => {
    return (
        <>
            <text x={(xStart + xEnd) / 2} y={y - HEADER_UNIT} fill="currentColor" fontSize="12" textAnchor="middle">
                <tspan x={(xStart + xEnd) / 2} dy="0" fontSize={14} fontWeight={600}>
                    {name}
                </tspan>
            </text>
            <line x1={xStart} x2={xStart} y1={y + HEADER_UNIT} y2={y} stroke="currentColor"></line>
            <line x1={xStart} x2={xEnd} y1={y} y2={y} stroke="currentColor"></line>
            <line x1={xEnd} x2={xEnd} y1={y} y2={y + HEADER_UNIT} stroke="currentColor"></line>
        </>
    );
};

type OpenChangeHandler = (
    open: boolean,
    info: {
        source: 'trigger' | 'menu';
    },
) => void;

export const GeoChartHeaderPicture: FC<{
    width: number;
    height: number;
}> = ({ width: fullWidth, height: fullHeight }) => {
    const meta = useMetaContext();
    const blockMenuStateRef = useRef(false);
    const { geoData, selectedPerforations, perforationData, testingData, selectedTestings } = useGeoData();

    const dispatch = useDispatch();
    const { toggleSelectedPerforation, toggleSelectedTesting, toggleAllPerforation } = dispatch.geoTest;

    if (!meta.picture) {
        throw new Error('picture is missing in GeoChartBody');
    }

    const { distanceToPixelsX } = meta.picture;
    const initialOpen: Record<number, boolean> = {};
    for (const well of geoData.wells) {
        initialOpen[well.wellId] = false;
    }
    const [open, setOpen] = useState<Record<number, boolean>>(initialOpen);
    const getHandleOpenChange =
        (wellId: number): OpenChangeHandler =>
        (nextOpen, info) => {
            if (info.source === 'menu') {
                blockMenuStateRef.current = true;
                setTimeout(() => {
                    blockMenuStateRef.current = false;
                }, 0);
            }

            if (info.source === 'trigger' || nextOpen) {
                if (nextOpen === false) {
                    setOpenKeys([]);
                }
                setTimeout(() => setOpen(prev => ({ ...prev, [wellId]: nextOpen })), 0);
            }
        };

    const handleOpenKeysChange = (nextOpenKeys: string[]) => {
        if (blockMenuStateRef.current) {
            return;
        }
        setOpenKeys(nextOpenKeys);
    };

    const [openKeys, setOpenKeys] = useState<string[] | undefined>();

    const wells: WellInfo[] = [];
    if (meta && geoData)
        for (let i = 0; i < geoData.wells.length; i++) {
            let isVerticalName = false;
            if (
                (i < geoData.wells.length - 1 &&
                    distanceToPixelsX(geoData.wells[i + 1].x) - distanceToPixelsX(geoData.wells[i].x) < 50) ||
                (i > 0 && distanceToPixelsX(geoData.wells[i - 1].x) - distanceToPixelsX(geoData.wells[i].x) > -50)
            ) {
                isVerticalName = true;
            }

            wells.push({
                x: distanceToPixelsX(geoData.wells[i].x),
                id: geoData.wells[i].wellId,
                name: geoData.wells[i].name,
                isBadVersion: Boolean(geoData.wells[i].isBadVersion),
                isVerticalName: isVerticalName,
                lengthening: geoData.wells[i].lengthening ? `Удл. ${geoData.wells[i].lengthening.toFixed(2)}` : '',
                altitude: geoData.wells[i].altitude ? `А.р. ${geoData.wells[i].altitude.toFixed(2)}` : '',
            });
        }
    const minusY = (well: WellInfo) => (well.isVerticalName ? 2 : 30);
    const getTransform = (well: WellInfo) =>
        well.isVerticalName ? `rotate(-90 ${well.x ?? ''} ${fullHeight - WELL_HEIGHT - 4})` : '';
    const textAnchor = (well: WellInfo) => (well.isVerticalName ? 'start' : 'middle');

    const getTooltipInfo = (well: WellInfo) => (
        <>
            <div>{well.lengthening}</div>
            <div>{well.altitude}</div>
        </>
    );

    const fields: ParentInfo[] = [];
    const elevations: ParentInfo[] = [];

    if (geoData?.wells.length !== 0) {
        const wells = geoData?.wells ?? [];
        let currentFieldName = wells[0].fieldName;
        let currentKupolName = wells[0].kupolName;
        let firstFieldX = wells[0].x;
        let firstKupolX = wells[0].x;
        let prevWell = wells[0];

        for (let i = 1; i < wells.length; i++) {
            const well = wells[i];

            if (well.fieldName !== currentFieldName) {
                const xEnd = (prevWell.x + well.x) / 2;
                fields.push({
                    name: currentFieldName,
                    xStart: distanceToPixelsX(firstFieldX) - (fields.length === 0 ? WELL_WIDTH : -WELL_WIDTH),
                    xEnd: distanceToPixelsX(xEnd) - WELL_WIDTH,
                });
                currentFieldName = well.fieldName;
                firstFieldX = xEnd;
            }

            if (well.kupolName !== currentKupolName && currentKupolName) {
                const xEnd = (prevWell.x + well.x) / 2;
                elevations.push({
                    name: currentKupolName ?? 'null',
                    xStart: distanceToPixelsX(firstKupolX) - (fields.length === 0 ? WELL_WIDTH : -WELL_WIDTH),
                    xEnd: distanceToPixelsX(xEnd) - WELL_WIDTH,
                });
                currentKupolName = well.kupolName;
                firstKupolX = xEnd;
            }

            prevWell = well;
        }

        // Добавляем последнюю границу
        fields.push({
            name: currentFieldName,
            xStart: distanceToPixelsX(firstFieldX) - (fields.length === 0 ? WELL_WIDTH : -WELL_WIDTH),
            xEnd: distanceToPixelsX(prevWell.x) + WELL_WIDTH,
        });

        elevations.push({
            name: currentKupolName ?? 'null',
            xStart: distanceToPixelsX(firstKupolX) - (elevations.length === 0 ? WELL_WIDTH : -WELL_WIDTH),
            xEnd: distanceToPixelsX(prevWell.x) + WELL_WIDTH,
        });
    }

    const getDropdownItems = (wellId: number): Item[] => {
        const items = [
            testingData[wellId]?.length
                ? {
                      type: undefined,
                      title: 'Исследования',
                      itemKey: 'testing',
                      children: testingData[wellId].map(({ id, roof, bottom }) => ({
                          type: 'checkbox',
                          title: `Исследование от ${'d'}: ${Math.round(100 * roof) / 100}-${Math.round(100 * bottom) / 100}(${Math.round(100 * (bottom - roof)) / 100})`,
                          itemKey: id.toString(),
                          checked: selectedTestings[wellId]?.includes(id),
                          onChange: () => toggleSelectedTesting({ wellId, id }),
                      })),
                  }
                : null,

            perforationData[wellId]?.length
                ? {
                      type: undefined,
                      title: 'Перфорации/Изоляции',
                      itemKey: 'perforation',
                      children: [
                          {
                              type: 'checkbox',
                              title: 'Выбрать все',
                              itemKey: 'selectAll',
                              indeterminate:
                                  selectedPerforations[wellId]?.length > 0 &&
                                  selectedPerforations[wellId]?.length < perforationData[wellId].length,
                              checked: selectedPerforations[wellId]?.length > 0,
                              onChange: () => toggleAllPerforation({ wellId }),
                          },
                          ...perforationData[wellId]
                              .sort(({ roof: roofA }, { roof: roofB }) => roofA - roofB)
                              .map(({ id, dateTime, roof, bottom, type }) => ({
                                  type: 'checkbox',
                                  title: `${type === 'ISOLATION' ? 'Изоляция' : 'Перфорация'} от ${dateTime}: ${Math.round(100 * roof) / 100}-${Math.round(100 * bottom) / 100}(${Math.round(100 * (bottom - roof)) / 100})`,
                                  itemKey: id.toString(),
                                  checked: selectedPerforations[wellId]?.includes(id),
                                  onChange: () => toggleSelectedPerforation({ id, wellId }),
                              })),
                      ],
                  }
                : null,
        ].filter(Boolean) as Item[];
        return items;
    };

    return (
        <svg width={fullWidth} height={fullHeight}>
            {fields
                .filter(({ name }) => name !== 'null')
                .map(info => (
                    <ParentFigure key={info.name} {...info} y={fullHeight - 20 * HEADER_UNIT} />
                ))}
            {elevations
                .filter(({ name }) => name !== 'null')
                .map(info => (
                    <ParentFigure key={info.name} {...info} y={fullHeight - 14 * HEADER_UNIT} />
                ))}
            {wells.map(well => {
                if (well.x) {
                    const tooltipInfo = getTooltipInfo(well);
                    return (
                        <Fragment key={well.id}>
                            <DropdownWrapper
                                open={open[well.id]}
                                onOpenChange={getHandleOpenChange(well.id)}
                                dropdownItems={getDropdownItems(well.id)}
                                trigger={['contextMenu']}
                                openKeys={openKeys}
                                onOpenKeys={handleOpenKeysChange}
                            >
                                <Tooltip title={well.isVerticalName ? tooltipInfo : undefined}>
                                    <text
                                        x={well.x}
                                        y={fullHeight - WELL_HEIGHT - minusY(well)}
                                        fill="currentColor"
                                        fontSize="12"
                                        style={{ textAnchor: textAnchor(well) }}
                                        transform={getTransform(well)}
                                    >
                                        <tspan
                                            x={well.x}
                                            dy="0"
                                            fontSize={well.isVerticalName ? 12 : 14}
                                            fontWeight={600}
                                        >
                                            Скв. {well.name}
                                        </tspan>
                                        {!well.isVerticalName && (
                                            <>
                                                <tspan x={well.x} dy="14">
                                                    {well.lengthening}
                                                </tspan>
                                                <tspan x={well.x} dy="14">
                                                    {well.altitude}
                                                </tspan>
                                            </>
                                        )}
                                    </text>
                                </Tooltip>
                            </DropdownWrapper>

                            <line
                                x1={well.x}
                                x2={well.x - WELL_WIDTH / 3}
                                y1={fullHeight - WELL_HEIGHT}
                                y2={fullHeight}
                                stroke="currentColor"
                            ></line>
                            <line
                                x1={well.x}
                                x2={well.x + WELL_WIDTH / 3}
                                y1={fullHeight - WELL_HEIGHT}
                                y2={fullHeight}
                                stroke="currentColor"
                            ></line>
                            <line
                                x1={well.x - WELL_WIDTH / 3}
                                x2={well.x + WELL_WIDTH / 3}
                                y1={fullHeight}
                                y2={fullHeight}
                                stroke="currentColor"
                            ></line>
                            {well.isBadVersion && (
                                <>
                                    <line
                                        x1={well.x - 4}
                                        x2={well.x + 4}
                                        y1={fullHeight - 4 - 4}
                                        y2={fullHeight - 4 + 4}
                                        stroke="#e3000f"
                                        strokeWidth={2}
                                    ></line>
                                    <line
                                        x1={well.x + 4}
                                        x2={well.x - 4}
                                        y1={fullHeight - 4 - 4}
                                        y2={fullHeight - 4 + 4}
                                        stroke="#e3000f"
                                        strokeWidth={2}
                                    ></line>
                                </>
                            )}
                            <path
                                d={generateZigzagPath(well.x, fullHeight - WELL_HEIGHT, fullHeight, WELL_WIDTH / 3, 10)}
                                stroke="currentColor"
                                fill="none"
                            ></path>
                        </Fragment>
                    );
                }
            })}
        </svg>
    );
};

const generateZigzagPath = (
    xCenter: number,
    yTop: number,
    yBottom: number,
    halfBaseWidth: number,
    stepY: number,
): string => {
    const path = [`M ${xCenter},${yTop}`];
    let currentY = yTop;
    let direction = 1;
    while (currentY + stepY <= yBottom) {
        currentY += stepY;
        const heightRatio = (currentY - yTop) / (yBottom - yTop);
        const xOffset =
            direction === 1 ? halfBaseWidth * (1 - heightRatio) * 0.5 : halfBaseWidth * (1 - heightRatio) * 1.5;
        const nextX = xCenter + direction * xOffset;
        path.push(`L ${nextX},${currentY}`);
        direction *= -1;
    }
    path.push(`L ${xCenter},${yBottom}`);
    return path.join(' ');
};
